import type { StructureDetail, StructureListItem, StructureType } from './api';
/**
 * Shared Structure Report helpers used by both the interactive report
 * (`StructureReportPage`) and the print-optimised PDF page (`StructurePdfPage`).
 * Mirrors the legacy `report.blade.php` / `components/reports/structure.blade.php`
 * logic (field parsing, filter-image selection, SVG output population).
 */

export type Family = 'nyloplast' | 'precast' | 'castiron' | 'curbthroat';

/** snake_case key in `enabled_fields` / `extra_outputs` -> StructureDetail key. */
export const FIELD_LOOKUP: Record<string, keyof StructureDetail> = {
  grate_size_left_to_right: 'grateSizeLeftToRight',
  grate_size_front_to_back: 'grateSizeFrontToBack',
  clear_opening_left_to_right: 'clearOpeningLeftToRight',
  clear_opening_front_to_back: 'clearOpeningFrontToBack',
  grate_size_long_span: 'grateSizeLongSpan',
  grate_size_short_span: 'grateSizeShortSpan',
  clear_opening_long_span: 'clearOpeningLongSpan',
  clear_opening_short_span: 'clearOpeningShortSpan',
  curb_opening_width: 'curbOpeningWidth',
  catch_basin_wall_to_wall_inside_dimension: 'catchBasinWallToWallInsideDimension',
  grate_diameter: 'grateDiameter',
  frame_clear_opening_diameter: 'frameClearOpeningDiameter',
  basin_size: 'basinSize',
  extended_bottom_depth: 'extendedBottomDepth',
  extended_bottom_width: 'extendedBottomWidth',
};

export interface ReportField {
  key: keyof StructureDetail;
  label: string;
  svgId: string;
}

export function parseFieldsJson(json: string | null | undefined): ReportField[] {
  if (!json) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return [];
  }
  if (!parsed || typeof parsed !== 'object') return [];
  return Object.entries(parsed as Record<string, string>)
    .map(([snake, label]) => ({ key: FIELD_LOOKUP[snake], label, svgId: snake }))
    .filter((f): f is ReportField => !!f.key);
}

export function familyOf(type: StructureType | null): Family {
  if (!type) return 'nyloplast';
  if (type.isNyloplast) return 'nyloplast';
  if (type.isOpenThroat) return 'curbthroat';
  if (type.isCastIron) return 'castiron';
  return 'precast';
}

/** Mirrors the legacy structure::GetNameAttribute fallback used in reports. */
export function structureDisplayName(detail: StructureDetail): string {
  if (detail.nickname?.trim()) return detail.nickname;

  if (detail.partId != null && detail.partNumber) {
    const dimension = detail.frameClearOpeningDiameter
      ?? (detail.clearOpeningLeftToRight != null && detail.clearOpeningFrontToBack != null
        ? `${detail.clearOpeningLeftToRight}x${detail.clearOpeningFrontToBack}`
        : detail.clearOpeningLongSpan != null && detail.clearOpeningShortSpan != null
          ? `${detail.clearOpeningLongSpan}x${detail.clearOpeningShortSpan}`
          : detail.curbOpeningWidth ?? detail.basinSize);
    return `${detail.partNumber}-${detail.permanent ? 'Permanent' : 'Temporary'}-${dimension ?? 'invalid'}`;
  }

  if (detail.foundryName && detail.foundryPartNumber) {
    return `${detail.foundryName} ${detail.foundryPartNumber}`;
  }

  return `Structure #${detail.id}`;
}

/**
 * Default display name for a structure list item, mirroring the legacy
 * `structure::GetNameAttribute` fallback. When no nickname is set, builds a name
 * from the filter configuration (`{styleNumber}-{Permanent|Temporary}-{dimension}`
 * for castings, or `{foundry} {partNumber}` for foundry parts) instead of an
 * opaque `Structure #{id}`.
 */
export function structureListDisplayName(s: StructureListItem): string {
  if (s.nickname?.trim()) return s.nickname;

  if (s.foundryName && s.foundryPartNumber) {
    return `${s.foundryName} ${s.foundryPartNumber}`;
  }

  if (s.styleNumber) {
    const dimension = s.frameClearOpeningDiameter
      ?? (s.clearOpeningLeftToRight != null && s.clearOpeningFrontToBack != null
        ? `${s.clearOpeningLeftToRight}x${s.clearOpeningFrontToBack}`
        : s.clearOpeningLongSpan != null && s.clearOpeningShortSpan != null
          ? `${s.clearOpeningLongSpan}x${s.clearOpeningShortSpan}`
          : s.curbOpeningWidth ?? s.basinSize);
    return `${s.styleNumber}-${s.permanent ? 'Permanent' : 'Temporary'}-${dimension ?? 'invalid'}`;
  }

  return `Structure #${s.id}`;
}

/** Round/square FlexStorm filter sample image (legacy `<x-filterimage>` body). */
function flexstormFilterImage(round: boolean, permanent: boolean, oil: boolean): string {
  if (!permanent) {
    return round ? '/images/FlexstormFilter-Round-Temp.png' : '/images/FlexstormFilter-Square-Temp.png';
  }
  if (oil) {
    return round ? '/images/FlexstormFilter-Round-Oil.png' : '/images/FlexstormFilter-Square-Oil.png';
  }
  return round ? '/images/FlexstormFilter-Round.png' : '/images/FlexstormFilter-Square.png';
}

/** Mirrors the legacy `<x-filterimage>` Blade component. */
export function filterImageSrc(
  type: StructureType | null,
  permanent: boolean,
  oil: boolean,
): string | null {
  if (!type) return null;
  if (type.isNyloplast) {
    return permanent ? '/images/FlexStorm-Nyloplast.png' : '/images/FlexStorm-Nyloplast-Temp.png';
  }
  return flexstormFilterImage(type.isCastIronRound, permanent, oil);
}

/**
 * Filter sample image for a casting-based structure. Mirrors the legacy casting
 * report, which passes `:round="$structure->foundry_part->isround"` — where
 * `isround` is true when the foundry part has a grate-size diameter.
 */
export function castingFilterImageSrc(
  grateSizeDiameter: number | null,
  permanent: boolean,
  oil: boolean,
): string {
  return flexstormFilterImage(grateSizeDiameter != null, permanent, oil);
}

/**
 * Legacy `report.blade.php` PopulateSVGs: fill the embedded diagram's
 * `<text id="{field}_output">` / `{field}_output_2` nodes with the saved
 * dimensions. Only works for `<object>`-embedded SVGs (inner DOM reachable).
 * Enabled fields are always written; extra outputs only when > 0.
 */
export function populateDiagram(
  objects: Array<HTMLObjectElement | null>,
  detail: StructureDetail,
  type: StructureType,
) {
  const enabled = parseFieldsJson(type.enabledFields);
  const extra = parseFieldsJson(type.extraOutputs);
  for (const obj of objects) {
    const doc = obj?.contentDocument;
    if (!doc) continue;
    const apply = (fields: ReportField[], requirePositive: boolean) => {
      for (const f of fields) {
        const v = detail[f.key];
        if (typeof v !== 'number') continue;
        if (requirePositive && v <= 0) continue;
        const text = `${v} Inches`;
        for (const id of [`${f.svgId}_output`, `${f.svgId}_output_2`]) {
          const el = doc.getElementById(id);
          if (el) el.textContent = text;
        }
      }
    };
    apply(enabled, false);
    apply(extra, true);
  }
}

/**
 * Same as {@link populateDiagram} but for an inline SVG (or any DOM subtree)
 * rather than an `<object>`-embedded document. Used by the PDF page, which
 * inlines the drawing so html2canvas can rasterise it.
 */
export function populateSvgRoot(
  root: ParentNode,
  detail: StructureDetail,
  type: StructureType,
) {
  const enabled = parseFieldsJson(type.enabledFields);
  const extra = parseFieldsJson(type.extraOutputs);
  const apply = (fields: ReportField[], requirePositive: boolean) => {
    for (const f of fields) {
      const v = detail[f.key];
      if (typeof v !== 'number') continue;
      if (requirePositive && v <= 0) continue;
      const text = `${v} Inches`;
      for (const id of [`${f.svgId}_output`, `${f.svgId}_output_2`]) {
        const el = root.querySelector(`[id="${id}"]`);
        if (el) el.textContent = text;
      }
    }
  };
  apply(enabled, false);
  apply(extra, true);
}

/**
 * Scope a drawing SVG's inline `<style>` rules to a single SVG instance.
 *
 * The drawing SVGs (`/images/DRAWINGS/*.svg`) all reuse the same generic class
 * names (`.cls-1`, `.cls-2`, …) with per-file meanings. When several diagrams
 * are inlined into one document (the project PDF renders one SVG per structure),
 * those class rules are global and collide — the last inlined SVG's colours win,
 * turning earlier diagrams into solid black blobs. Prefixing every selector with
 * the owning SVG's unique id keeps each diagram's styling isolated. `@`-rules
 * (e.g. `@font-face`) must stay global so fonts still resolve, so they are left
 * untouched.
 */
export function scopeSvgCss(css: string, scopeSelector: string): string {
  return css.replace(/([^{}]+)\{([^{}]*)\}/g, (full, selector: string, body: string) => {
    const trimmed = selector.trim();
    if (trimmed.startsWith('@')) return full;
    const scoped = trimmed
      .split(',')
      .map((part) => `${scopeSelector} ${part.trim()}`)
      .join(', ');
    return `${scoped} {${body}}`;
  });
}
